import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  ShoppingCart,
  CreditCard,
  MessageSquare,
  AlertTriangle,
  X,
  Check,
  Bell,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { useNavigate } from "react-router-dom";
import useRequest from "./hooks/use-request";

type NotificationType = "order" | "payment" | "message" | "alert";

type Notification = {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type: NotificationType;
};

// Moved to a separate component for better organization
const NotificationIcon = ({ type }: { type: NotificationType }) => {
  switch (type) {
    case "order":
      return <ShoppingCart className="h-4 w-4 text-green-700" />;
    case "payment":
      return <CreditCard className="h-4 w-4 text-blue-600" />;
    case "message":
      return <MessageSquare className="h-4 w-4 text-indigo-600" />;
    case "alert":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    default:
      return <AlertTriangle className="h-4 w-4 text-gray-500" />;
  }
};

// Notification item component for better code organization
const NotificationItem = ({
  notification,
  onRead,
}: {
  notification: Notification;
  onRead: (id: string) => void;
}) => (
  <div
    onClick={() => onRead(notification.id)}
    className={`px-4 py-3 cursor-pointer transition-colors duration-200 ${
      !notification.read
        ? "bg-gray-50 hover:bg-gray-100"
        : "bg-white hover:bg-gray-50"
    }`}
  >
    <div className="flex items-start gap-3">
      {!notification.read && (
        <span className="h-2 w-2 bg-green-500 rounded-full mt-2"></span>
      )}

      {/* Icon */}
      <div className="flex-shrink-0 p-2 rounded-full bg-gray-100">
        <NotificationIcon type={notification.type} />
      </div>

      {/* Title and Message */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center mb-1">
          <p className="text-sm font-semibold text-gray-800">
            {notification.title}
          </p>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">
            {notification.time}
          </span>
        </div>
        <p className="text-xs text-gray-500">{notification.message}</p>
      </div>
    </div>
  </div>
);

const NotificationsPopover = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"all" | "unread">("all");
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const userToken = localStorage.getItem("token") || "";

  // Create API request hooks
  const { makeRequest: getNotifications } = useRequest(
    "/notifications",
    "GET",
    {
      Authorization: `Bearer ${userToken}`,
    }
  );

  useEffect(() => {
    const fetchNotifications = async () => {
      const [response] = await getNotifications();
      if (response.status) {
        setNotifications(response.data?.notifications);
      }
    };
    fetchNotifications();
  }, []);

  // Use useMemo to calculate derived state
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const filteredNotifications = useMemo(
    () =>
      tab === "all" ? notifications : notifications.filter((n) => !n.read),
    [tab, notifications]
  );

  // Use useCallback for event handlers
  const markAllAsRead = useCallback(() => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) => ({ ...n, read: true }))
    );
  }, []);

  const markOneAsRead = useCallback((id: string) => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  // Navigate to notifications page
  const viewAllNotifications = useCallback(() => {
    navigate("/notifications");
    setOpen(false);
  }, [navigate]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      {open && (
        <PopoverContent
          className="w-96 p-0 rounded-lg shadow-lg bg-white"
          align="end"
        >
          {/* Header Section */}
          <div className="flex flex-col">
            <div className="flex items-center justify-between px-4 pt-4">
              <h4 className="font-semibold text-lg text-gray-900">
                Notifications
              </h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0 text-gray-500 hover:text-gray-800"
                onClick={() => setOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Tabs and Action */}
            <div className="flex items-center justify-between border-b mt-4 px-4">
              {/* Tabs */}
              <div className="flex gap-6 relative">
                <button
                  onClick={() => setTab("all")}
                  className={`pb-3 text-sm font-medium ${
                    tab === "all"
                      ? "text-black border-b-2 border-green-700"
                      : "text-gray-400 hover:text-black"
                  }`}
                >
                  All
                </button>

                <button
                  onClick={() => setTab("unread")}
                  className={`pb-3 text-sm font-medium flex items-center gap-1 ${
                    tab === "unread"
                      ? "text-black border-b-2 border-green-700"
                      : "text-gray-400 hover:text-black"
                  }`}
                >
                  Unread
                  {unreadCount > 0 && (
                    <span className="text-xs font-semibold bg-green-100 text-green-700 rounded-full px-2 py-0.5">
                      {unreadCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Mark all as read - Only show if there are unread notifications */}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-black p-0 h-auto"
                  onClick={markAllAsRead}
                >
                  <Check className="h-3 w-3" />
                  Mark all as read
                </Button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <ScrollArea className="h-[320px]">
            <div className="divide-y">
              {filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={markOneAsRead}
                  />
                ))
              ) : (
                <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
                  No notifications
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              className="w-full text-xs text-green-700 hover:text-black"
              onClick={viewAllNotifications}
            >
              View all notifications
            </Button>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
};

export default NotificationsPopover;
